import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./ClientContractDelete.css";

export default function ClientContractDeleteDialog({
  open,
  onClose,
  record,
  onDeleted,
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const rawRecord = useMemo(() => record ?? null, [record]);

  /*
   * La deschiderea dialogului:
   * - resetăm starea de loading
   * - resetăm eventualele erori anterioare
   */
  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
  }, [open]);

  /*
   * Funcția care execută efectiv ștergerea contractului.
   * - face request DELETE către backend
   * - dacă reușește, apelează callback-ul onDeleted
   * - dacă eșuează, afișează mesaj de eroare
   */
  const handleDelete = async () => {
    if (!rawRecord?.id) return;

    setError("");
    setDeleting(true);

    try {
      await axiosInstance.delete(`/client-contracts/${rawRecord.id}/`);
      onDeleted?.();
    } catch (e) {
      setError(
        "Cannot delete this contract because it has dependencies in other records. Please remove or update those dependencies first."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className="contract-delete-dialog"
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header-ul dialogului - nu se afișează dacă există eroare */}
      {!error && (
        <DialogTitle className="cd-header">
          <Box className="cd-header-content">
            <Box className="cd-title-section">
              <WarningAmberIcon className="cd-warning-icon" />
              <Typography variant="h5" component="span" className="cd-title">
                Delete Contract
              </Typography>
            </Box>

            {/* Buton de închidere */}
            <IconButton
              onClick={onClose}
              className="cd-close-btn"
              size="small"
              disabled={deleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent className="cd-content">
        {error ? (
          /**
           * Dacă există eroare:
           * - afișăm alert de tip error
           * - explicăm motivul pentru care nu se poate șterge
           */
          <Alert
            severity="error"
            className="cd-error-alert"
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body1" fontWeight={500}>
              Unable to Delete Contract
            </Typography>
            <Typography variant="body2" className="cd-error-message">
              {error}
            </Typography>
          </Alert>
        ) : (
          /**
           * Dacă NU există eroare:
           * - afișăm mesajul de confirmare pentru ștergere
           * - detalii despre consecințe
           */
          <Box className="cd-confirm-content">
            {rawRecord?.nr && (
              <Box className="cd-number-container">
                <Typography variant="subtitle2" className="cd-number-label">
                  Contract Number
                </Typography>
                <Typography variant="h6" className="cd-number-value">
                  {rawRecord.nr}
                </Typography>
              </Box>
            )}

            <Typography variant="body1" className="cd-warning-text">
              This action is permanent and cannot be undone.
            </Typography>

            <Box className="cd-consequences">
              <Typography variant="body2" className="cd-consequence-item">
                • The contract will be permanently deleted from the system
              </Typography>
              <Typography variant="body2" className="cd-consequence-item">
                • All associated contract data will be removed
              </Typography>
              <Typography variant="body2" className="cd-consequence-item">
                • This change cannot be reversed
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="cd-actions">
        {error ? (
          // Dacă există eroare: afișăm doar un buton de confirmare ("Got It")

          <Button
            onClick={onClose}
            variant="contained"
            className="cd-btn cd-btn-ok"
          >
            Got It
          </Button>
        ) : (
          <>
            {/* Buton Cancel - închide dialogul */}
            <Button
              onClick={onClose}
              disabled={deleting}
              variant="outlined"
              className="cd-btn cd-btn-cancel"
            >
              Cancel
            </Button>

            {/* Buton Delete - declanșează ștergerea */}
            <Button
              onClick={handleDelete}
              disabled={deleting || !rawRecord?.id}
              variant="contained"
              className="cd-btn cd-btn-delete"
              startIcon={
                deleting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              autoFocus
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}